<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :loading-message="status.loadingMessage"
    :is-loading="status.isLoading"
    button-location="top"
    @submit="getCustomers()"
  >
    <template #title>
      User Search
    </template>

    <template #top-form>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Search Type*</label>
        <div class="col-sm-4">
          <dropdown-list
            v-model="searchConditions.type"
            :options="availableSearchTypes"
          />
        </div>
      </div>

      <div
        class="row"
        :class="{ 'align-items-center': !isSearchByAddress && !isSearchByFullName }"
      >
        <label
          v-if="!isSearchByAddress"
          class="col-sm-3 control-label"
          :class="{ 'pt-10': isSearchByFullName }"
        >
          {{ searchValueFieldLbl }}{{ isPartnerDashboard ? '' : '*' }}
        </label>

        <phone-number-input
          v-if="isSearchByPhoneNumber"
          v-model="searchConditions.query"
          class="col-sm-6"
          :page-id="pageId"
          :required="!isPartnerDashboard"
        />

        <div
          v-else-if="isSearchByCreatedAt"
          class="col-sm-6"
        >
          <date-picker
            v-model="timeSpanValue"
            format="YYYY-MM-DD"
            type="daterange"
            required
          />
        </div>

        <div
          v-else-if="!isSearchByAddress"
          :class="isSearchByFullName ? 'col-sm-3 col-6' : 'col-sm-6'"
        >
          <sanitized-input
            id="query"
            v-model="searchConditions.query"
            :required="!isPartnerDashboard"
            :placeholder="isSearchByFullName ? 'Please input first name' : 'Please input search value'"
          />
          <span
            v-if="isSearchByFullName"
            class="help-block"
          >First Name*</span>
        </div>

        <div
          v-if="isSearchByFullName"
          class="col-sm-3 col-6"
        >
          <sanitized-input
            id="lastName"
            v-model="searchConditions.query1"
            placeholder="Please input last name"
            :required="!isPartnerDashboard"
          />
          <span class="help-block">Last Name*</span>
        </div>
        <div
          v-if="isSearchByAddress"
          class="col-sm-8 offset-sm-1"
        >
          <address-section
            v-model="address"
            :page-id="pageId"
          />
        </div>
      </div>
      <div
        v-if="isSearchByCard4Digits"
        class="row align-items-center"
      >
        <label class="col-sm-3 control-label">Card Zip</label>
        <div class="col-sm-6">
          <sanitized-input
            id="cardZip"
            v-model="searchConditions.query1"
            placeholder="Please input card zip"
          />
        </div>
      </div>

      <div class="form-submit-button-group pb-10 mobile-pb-10">
        <p-button
          v-if="isPartnerDashboard"
          type="purple"
          round
          :disabled="status.isLoading"
          @click="isAddPartnerCallerDialogVisible = true"
        >
          + Enroll New User
        </p-button>

        <p-button
          type="purple"
          round
          native-type="submit"
          :disabled="isBtnDisabled"
        >
          Search
        </p-button>

        <p-button
          v-if="isPartnerDashboard"
          type="info"
          round
          size="md"
          :disabled="isBtnDisabled || callers.length === 0"
          @click="downloadCallersJSON2CSV(1)"
        >
          Download
        </p-button>

        <p-button
          v-if="isPartnerDashboard"
          type="yellow"
          round
          :disabled="status.isLoading"
          @click="resetForm"
        >
          Reset
        </p-button>
      </div>

      <div class="offset-3 col-6">
        <pagination
          :is-loading="status.isLoading"
          :current-page="status.currentPage"
          :total-pages="status.totalPages"
          @load-page-data="getCustomers"
        />
      </div>

      <message-html :result-html="status.resultHtml" />
    </template>

    <template #content>
      <table-view-container
        :page-id="pageId"
        :data="callers"
        :table-columns="availableTableColumns"
        :fixed-column-counts="3"
      >
        <template #right-fixed-columns>
          <el-table-column
            v-if="callers.length > 0 && isPartnerDashboard"
            fixed="right"
            label="Action"
            width="50"
          >
            <template #default="scope">
              <el-tooltip
                class="item"
                effect="dark"
                content="Remove customer from partnership"
                placement="bottom-end"
              >
                <div
                  class="info-icon"
                  @click="removeCustomer(scope.row)"
                >
                  <i class="fa fa-trash" />
                </div>
              </el-tooltip>
            </template>
          </el-table-column>

          <el-table-column
            v-if="callers.length"
            fixed="right"
            label="Info"
            width="50"
          >
            <template #default="scope">
              <el-tooltip
                class="item"
                effect="dark"
                content="Pull the customer info on RightPanel"
                placement="bottom-end"
              >
                <div
                  class="info-icon nl-icon"
                  @click="pullUpCustomer(scope.row)"
                >
                  <i class="fa fa-user" />
                </div>
              </el-tooltip>
            </template>
          </el-table-column>
        </template>
      </table-view-container>
    </template>

    <add-partner-caller-dialog
      v-if="isPartnerDashboard"
      v-model:visible="isAddPartnerCallerDialogVisible"
    />
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, reactive, computed, watch, onMounted, toRefs, defineAsyncComponent } from 'vue';
import { useRouter } from 'vue-router';
import delay from 'yoctodelay';
import events from '@/events';

import util from '@/Utils/util';
import { sendGet } from '@/Utils/api';
import { useDashboard } from '@/use/app';
import { useCustomer } from '@/use/customer';
import { useAddress } from '@/use/address';
import { useSwalDialog } from '@/use/swalDialog';
import { usePartner } from '@/use/partner';
import { useProfile } from '@/use/profile';
import { CustomLocationType } from '@/models/address';
import { ListType } from '@/models/app';
import {
  partnerExtraCallerTableColumnsInPartnerDB,
  callerTableColumnsInOperatorDB,
  callerTableColumnsInPartnerDB,
  DIAMOND_BAR_SEARCH_PARTNERS,
  PARTNER_CALLER_SEARCH_COLUMNS,
  SUSPENSION_PERIOD_FIELDS,
} from '@/constants/caller';
import { applyPartnerFieldLabels, findPartnerField } from '@/Utils/partners/partnerFieldConfig';
import { partnerCallerService } from '@/services/partnerCaller';

const AddPartnerCallerDialog = defineAsyncComponent(() => import('./Partners/AddPartnerCallerDialog.vue') as any);

const pageId = 'customer-search';
const paginationLimit: number = 30;
const downloadPaginationLimit: number = 100;

const operatorSearchTypeList: ListType[] = [
  { id: 1, key: 'phoneNumber', label: 'Phone Number' },
  { id: 2, key: 'lovedOnePhoneNumber', label: 'Loved One Phone Number' },
  { id: 3, key: 'lovedOneEmail', label: 'Loved One Email' },
  { id: 4, key: 'rideNumber', label: 'Ride Number' },
  { id: 5, key: 'scheduledRideNumber', label: 'Scheduled Ride Number' },
  { id: 6, key: 'requestId', label: 'Request ID' },
  { id: 7, key: 'callerId', label: 'Caller ID' },
  { id: 8, key: 'fullName', label: 'Full Name' },
  { id: 9, key: 'firstName', label: 'First Name' },
  { id: 10, key: 'lastName', label: 'Last Name' },
  { id: 11, key: 'email', label: 'Email' },
  { id: 12, key: 'address', label: 'Address' },
  { id: 13, key: 'referralCode', label: 'Referral Code' },
  { id: 14, key: 'createdAt', label: 'Created At' },
];

const stripeCardSearchType: ListType = {
  id: 15,
  key: 'cardLast4',
  label: 'Card Last 4 Digits',
};

const partnerSearchTypeList: ListType[] = [
  { id: 1, key: '', label: 'All Types' },
  { id: 2, key: 'phoneNumber', label: 'Phone Number' },
  { id: 3, key: 'lovedOnePhoneNumber', label: 'Loved One Phone Number' },
  { id: 4, key: 'lovedOneEmail', label: 'Loved One Email' },
  { id: 5, key: 'fullName', label: 'Full Name' },
  { id: 6, key: 'firstName', label: 'First Name' },
  { id: 7, key: 'lastName', label: 'Last Name' },
  { id: 8, key: 'email', label: 'Email' },
  { id: 9, key: 'address', label: 'Address' },
  { id: 10, key: 'externalPartnerCallerId', label: 'External Caller ID' },
  { id: 11, key: 'partnerEmergencyContactPhoneNumber', label: 'Partner Emergency Contact Phone Number' },
  { id: 12, key: 'partnerEmergencyContactEmail', label: 'Partner Emergency Contact Email' },
];

const router = useRouter();

const { userRoles } = useProfile();
const { isPartnerDashboard } = useDashboard();
const { customerPhoneNumber, formatCustomerInfo } = useCustomer();
const { showSuccessPopupDialog, showErrorPopupDialog, showConfirmPopupDialog } = useSwalDialog();
const { trimAddress, getFormattedAddress } = useAddress();
const { partnerId, partnerCode, partnerInfo, partnerFieldConfig, isCvrcOrAcrc } = usePartner();
const { isAdmin, isDirector, isSupervisor, isTeamLead } = toRefs(userRoles);

const searchConditions: Ref<any> = ref({
  type: isPartnerDashboard.value ? '' : 'phoneNumber',
  query: '',
  query1: '',
  startDate: '',
  endDate: '',
});
const address: Ref<CustomLocationType> = ref({
  streetAddress: '',
  streetAddress2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
});
const callers: Ref<any> = ref([]);
const downloadData = ref('');
const isAddPartnerCallerDialogVisible = ref(false);
const status = reactive({
  isLoading: false,
  loadingMessage: 'Loading Callers...',
  resultHtml: '',
  currentPage: 1,
  totalPages: 1,
});
const timeSpanValue: Ref<any> = ref(null);

const hasSpecialPermission = computed(() => isAdmin.value || isSupervisor.value || isDirector.value || isTeamLead.value);

const availableOperatorSearchTypeList = computed(() => {
  if (hasSpecialPermission.value) {
    return [...operatorSearchTypeList, stripeCardSearchType];
  }

  return operatorSearchTypeList;
});

const availableSearchTypes = computed(() =>
  isPartnerDashboard.value
    ? applyPartnerFieldLabels(partnerFieldConfig.value, partnerSearchTypeList)
    : availableOperatorSearchTypeList.value,
);

const availableTableColumns = computed(() => {
  const partnerColumns = [...callerTableColumnsInPartnerDB, ...(partnerExtraCallerTableColumnsInPartnerDB[partnerCode.value] || [])];

  return isPartnerDashboard.value ? applyPartnerFieldLabels(partnerFieldConfig.value, partnerColumns) : callerTableColumnsInOperatorDB;
});

/** Export columns of a partner with a field config, in configured order; undefined for partners without one. */
const configuredPartnerColumns = computed<string[] | undefined>(() =>
  partnerInfo.value?.fieldConfig
    ?.map((entry: { field: string }) => entry.field)
    .filter((field: string) => PARTNER_CALLER_SEARCH_COLUMNS.includes(field)),
);

const isSearchByAddress = computed(() => searchConditions.value.type === 'address');
const isSearchByFullName = computed(() => searchConditions.value.type === 'fullName');

const isSearchByPhoneNumber = computed(
  () => searchConditions.value.type === 'phoneNumber' || searchConditions.value.type === 'lovedOnePhoneNumber',
);

const isSearchByCreatedAt = computed(() => searchConditions.value.type === 'createdAt');
const isSearchByCard4Digits = computed(() => searchConditions.value.type === 'cardLast4');

const isBtnDisabled = computed(() => {
  if (status.isLoading) {
    return true;
  }

  if (isSearchByPhoneNumber.value && !util.isPhoneNumberValid(searchConditions.value.query)) {
    return true;
  }

  return false;
});

const searchValueFieldLbl = computed(() => {
  const searchType = availableSearchTypes.value.find((item) => item.key === searchConditions.value.type);

  return searchType?.label || 'Search Value';
});

const makeSearchQuery = (offset: number) => {
  let query: any = {
    type: searchConditions.value.type,
    offset,
    limit: paginationLimit,
  };

  if (isSearchByFullName.value) {
    query = {
      ...query,
      firstName: searchConditions.value.query,
      lastName: searchConditions.value.query1,
    };
  } else if (isSearchByAddress.value) {
    address.value = trimAddress(address.value);

    query = {
      ...query,
      streetAddress: address.value.streetAddress,
      streetAddress2: address.value.streetAddress2,
      city: address.value.city,
      state: address.value.state,
      zip: address.value.zip,
    };
  } else if (isSearchByCreatedAt.value) {
    query = {
      ...query,
      startDate: searchConditions.value.startDate,
      endDate: searchConditions.value.endDate,
    };
  } else if (isSearchByCard4Digits.value) {
    query = {
      ...query,
      q: searchConditions.value.query,
      cardZipCode: searchConditions.value.query1,
    };
  } else {
    query = {
      ...query,
      q: searchConditions.value.query,
    };
  }

  if (isPartnerDashboard.value) {
    const idColumn = 'id';
    const commonColumns =
      'firstName,lastName,phoneNumber,phoneNumber2,homeNumber,email,dob,familyPhoneNumber,familyEmail,familyFirstName,familyLastName,streetAddress,streetAddress2,city,state,zip,mailingStreetAddress,mailingStreetAddress2,mailingCity,mailingState,mailingZip,mailingCountry,homeNote,gourmetHomeNote,infoForDriver,aName,aPhone,aLocation,aNote,bName,bPhone,bLocation,bNote,cName,cPhone,cLocation,cNote,createdAt,timeZone,credits,partnerCredits,partnerRides,isNoBigCars,isVisuallyImpaired,isCane,isTraveling, isElbowAssist,isWalker,isWheelchair,isHardOfHearing,isServiceDog,isOxygenTank,isPoorMemory,isVerballyImpaired,partnerNote';
    const customPartnerColumns =
      'externalPartnerCallerId,preAuthorizationNumber,partnerSuspensionDate,partnerEmergencyContactPhoneNumber,partnerEmergencyContactEmail';

    query = {
      ...query,
      partnerId: partnerId.value,
      fields: configuredPartnerColumns.value
        ? [idColumn, ...configuredPartnerColumns.value, commonColumns].join(',')
        : [idColumn, commonColumns, customPartnerColumns].join(','),
    };

    if (DIAMOND_BAR_SEARCH_PARTNERS.includes(partnerCode.value)) {
      query.fields = [idColumn, commonColumns, customPartnerColumns, SUSPENSION_PERIOD_FIELDS.join(',')].join(',');
    }
  }

  return query;
};

const getCustomers = async (currentPage: number = 1) => {
  status.resultHtml = '';
  status.loadingMessage = 'Loading Callers...';
  status.currentPage = currentPage;

  const offset = (currentPage - 1) * paginationLimit;
  const query = makeSearchQuery(offset);

  status.isLoading = true;
  try {
    const response = await sendGet('callers/search', query);
    status.totalPages = Math.ceil(response.data.count / paginationLimit) || 1;

    callers.value = response.data.rows.map((customer: any, index: number) => {
      const formattedCustomer = formatCustomerInfo(customer);
      formattedCustomer.index = (currentPage - 1) * paginationLimit + index + 1;
      formattedCustomer.homeAddress = getFormattedAddress(customer);
      formattedCustomer.mailingAddress = customer.mailingStreetAddress
        ? getFormattedAddress({
            streetAddress: customer.mailingStreetAddress,
            streetAddress2: customer.mailingStreetAddress2,
            city: customer.mailingCity,
            state: customer.mailingState,
            zip: customer.mailingZip,
            country: customer.mailingCountry,
          })
        : '';

      return formattedCustomer;
    });
  } catch (error) {
    status.resultHtml = util.getErrorResponse(error);
    status.totalPages = 1;
    status.currentPage = 1;
    callers.value = [];
  }
  status.isLoading = false;
};

// Diamond Bar exports also carry the subscription period, which isn't a partner-caller field
const subscriptionPeriodCsvLabels: Record<string, string> = {
  periodStartAt: 'Start Date',
  expectedPeriodEndAt: 'Expected End Date',
};

/** Replaces configured partner-caller column names in the CSV header with their labels. */
const labelCsvHeader = (header: string) =>
  header
    .split(',')
    .map((cell) => {
      const column = cell.replace(/^"|"$/g, '');
      const label = findPartnerField(partnerFieldConfig.value, column)?.label ?? subscriptionPeriodCsvLabels[column];

      return label ? `"${label.replace(/#/g, '').trim()}"` : cell;
    })
    .join(',');

const downloadCallersJSON2CSV = async (pageIndex: number = 1) => {
  status.loadingMessage = 'Please wait, while we prepare your download...';
  const offset = (pageIndex - 1) * downloadPaginationLimit;

  let query = makeSearchQuery(offset);

  query = {
    ...query,
    isDownloadCsv: true,
    sort: 'lastName',
    limit: downloadPaginationLimit,
  };

  if (DIAMOND_BAR_SEARCH_PARTNERS.includes(partnerCode.value)) {
    query.fields = [query.fields, 'periodStartAt', 'expectedPeriodEndAt'].filter(Boolean).join(',');
  }

  status.isLoading = true;
  try {
    const response = await sendGet('callers/search', query);
    // '#' is not encoded by encodeURI and would truncate the data-URI download
    const replacedCallers: string = response.data.replace(/#/g, '');
    const lines = replacedCallers.split('\n');

    for (const [index, dataString] of lines.entries()) {
      if (index !== 0 || (index === 0 && pageIndex === 1)) {
        const isHeader = index === 0 && pageIndex === 1;
        if (isHeader) {
          downloadData.value = '';
          downloadData.value += labelCsvHeader(dataString) + '\n';
        } else {
          downloadData.value += dataString + '\n';
        }
      }
    }

    if (lines.length >= downloadPaginationLimit + 1) {
      await downloadCallersJSON2CSV(pageIndex + 1);
    } else {
      util.downloadJSONToCSV(downloadData.value, `${partnerCode.value}-Callers.csv`);
      status.isLoading = false;
    }
  } catch (error) {
    status.resultHtml = util.getErrorResponse(error);
  }
};

const removeCustomer = async (row: any) => {
  try {
    const isRemove = await showConfirmPopupDialog({
      title: 'Are you sure to remove the customer from partnership?',
      confirmButtonText: 'Remove',
    });

    if (!isRemove) {
      return;
    }

    status.loadingMessage = 'Removing caller from partnership...';
    status.isLoading = true;

    await partnerCallerService.removePartnerCaller({
      partnerId: partnerId.value,
      callerId: row.id,
    });

    showSuccessPopupDialog({
      text: 'The customer has been removed from the partnership successfully.',
    });
    getCustomers();
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  status.isLoading = false;
};

const pullUpCustomer = async (row: any) => {
  const phoneNumber = row?.phoneNumber || row?.phoneNumber2 || row?.homeNumber || row?.receiptPhoneNumber;

  if (phoneNumber !== customerPhoneNumber.value) {
    router.push({
      name: isPartnerDashboard.value ? 'PartnerCustomerSearch' : 'CustomerSearch',
      query: { phoneNumber },
    });

    await delay(100);

    events.emit('refresh-right-panel');
  }
};

const resetForm = () => {
  searchConditions.value = {
    type: '',
    query: '',
    query1: '',
    startDate: '',
    endDate: '',
  };

  callers.value = [];
  timeSpanValue.value = null;
  status.resultHtml = '';
  status.totalPages = 1;
  status.currentPage = 1;
};

watch(timeSpanValue, (value: any) => {
  if (value) {
    searchConditions.value.startDate = util.formatDateTime(value[0], 'YYYY-MM-DD');
    searchConditions.value.endDate = util.formatDateTime(value[1], 'YYYY-MM-DD');
  } else {
    searchConditions.value.startDate = '';
    searchConditions.value.endDate = '';
  }
});

onMounted(() => {
  util.moveToTop();

  if (partnerId.value) {
    getCustomers();
  }
});
</script>

<style lang="scss">
.customer-search {
  .info-icon {
    color: #ee682d;
  }
}
</style>
